import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { HiCheck, HiClock } from 'react-icons/hi';
import { useSelector } from 'react-redux';

import * as S from '../ClientProfile.styled';

import { PROFILE_SECTION_GROUPS, type ProfileSectionMeta } from '../profileSections';
import { RootState } from 'redux/store';

interface ProfileSectionNavProps {
    sections: ProfileSectionMeta[];
    selectedKey: string;
    completedByKey: Record<string, boolean>;
    editMode: boolean;
    completedCount: number;
    canUpdateAssessmentDate: boolean;
    isUpdatingAssessmentDate: boolean;
    onSelect: (key: string) => void;
    onUpdateAssessmentDate: () => void;
}

const ProfileSectionNav = ({
    sections,
    selectedKey,
    completedByKey,
    editMode,
    completedCount,
    canUpdateAssessmentDate,
    isUpdatingAssessmentDate,
    onSelect,
    onUpdateAssessmentDate,
}: ProfileSectionNavProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const total = sections.length;
    const percent = total === 0 ? 0 : Math.round((completedCount / total) * 100);
    const progressLabel = t('sectionsCompleted', {
        lng: language,
        completed: completedCount,
        total,
        defaultValue: `${completedCount} / ${total} complete`,
    });

    const handleSelect = (section: ProfileSectionMeta) => {
        if (editMode && section.disabledWhenEditing) {
            return;
        }
        onSelect(section.key);
    };

    const progress = (
        <S.SideBarProgress>
            <S.SideBarProgressLabel>{progressLabel}</S.SideBarProgressLabel>
            <S.SideBarProgressTrack>
                <S.SideBarProgressFill $percent={percent} />
            </S.SideBarProgressTrack>
            {canUpdateAssessmentDate && (
                <S.UpdateAssessmentDateButton
                    type="button"
                    disabled={isUpdatingAssessmentDate}
                    onClick={onUpdateAssessmentDate}
                >
                    <HiClock aria-hidden="true" size={13} />
                    {t(
                        isUpdatingAssessmentDate
                            ? 'updatingAssessmentCompletionDate'
                            : 'updateAssessmentCompletionDate',
                        { lng: language }
                    )}
                </S.UpdateAssessmentDateButton>
            )}
        </S.SideBarProgress>
    );

    const renderItem = (section: ProfileSectionMeta, variant: 'rail' | 'chip') => {
        const isActive = selectedKey === section.key;
        const isDone = !!completedByKey[section.key];
        const isDisabled = editMode && !!section.disabledWhenEditing;
        const Item = variant === 'chip' ? S.SectionChip : S.SideBarItem;
        const item = (
            <Item
                type="button"
                $isActive={isActive}
                $isDisabled={isDisabled}
                disabled={isDisabled}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => handleSelect(section)}
            >
                <S.SideBarItemText>{section.label}</S.SideBarItemText>
                <S.SideBarDot
                    $done={isDone}
                    aria-label={
                        isDone ? t('done', { lng: language, defaultValue: 'Done' }) : undefined
                    }
                >
                    {isDone && <HiCheck size={11} />}
                </S.SideBarDot>
            </Item>
        );

        if (section.disabledWhenEditing && section.disabledTooltip) {
            return (
                <Tooltip
                    key={section.key}
                    title={section.disabledTooltip}
                    placement={variant === 'chip' ? 'bottom' : 'right'}
                    disableInteractive
                >
                    <S.NavItemWrap $chip={variant === 'chip'}>{item}</S.NavItemWrap>
                </Tooltip>
            );
        }

        return (
            <S.NavItemWrap key={section.key} $chip={variant === 'chip'}>
                {item}
            </S.NavItemWrap>
        );
    };

    return (
        <>
            <S.MobileNav aria-label={t('sections', { lng: language, defaultValue: 'Sections' })}>
                {progress}
                <S.ChipRail>{sections.map((section) => renderItem(section, 'chip'))}</S.ChipRail>
            </S.MobileNav>
            <S.SideBarSection>
                {progress}
                {PROFILE_SECTION_GROUPS.map((group) => {
                    const groupSections = sections.filter((section) => section.group === group.id);
                    if (groupSections.length === 0) {
                        return null;
                    }
                    return (
                        <S.SideBarGroup key={group.id}>
                            <S.SideBarGroupLabel>
                                {t(group.labelKey, { lng: language })}
                            </S.SideBarGroupLabel>
                            {groupSections.map((section) => renderItem(section, 'rail'))}
                        </S.SideBarGroup>
                    );
                })}
            </S.SideBarSection>
        </>
    );
};

export default ProfileSectionNav;
